import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  BRUTALIST_DARK_THEME,
  BRUTALIST_THEME,
  CYBER_DARK_THEME,
  CYBER_THEME,
  DARK_THEME,
  DEFAULT_THEME,
  DUOLINGO_DARK_THEME,
  DUOLINGO_THEME,
  MATERIAL_YOU_DARK_THEME,
  MATERIAL_YOU_THEME,
  MINIMAL_THEME,
  ThemeTokens,
} from '../../../styles/config/theme-config';
import {
  ANT_DARK_THEME,
  ANT_THEME,
  CARBON_DARK_THEME,
  CARBON_THEME,
  CUPERTINO_DARK_THEME,
  CUPERTINO_THEME,
  DISCORD_DARK_THEME,
  DISCORD_THEME,
  EDITORIAL_DARK_THEME,
  EDITORIAL_THEME,
  FLUENT_DARK_THEME,
  FLUENT_THEME,
  LINEAR_DARK_THEME,
  LINEAR_THEME,
  MEMPHIS_DARK_THEME,
  MEMPHIS_THEME,
  NOTION_DARK_THEME,
  NOTION_THEME,
  RETRO_TERMINAL_DARK_THEME,
  RETRO_TERMINAL_THEME,
  STRIPE_DARK_THEME,
  STRIPE_THEME,
  SWISS_DARK_THEME,
  SWISS_THEME,
  VAPORWAVE_DARK_THEME,
  VAPORWAVE_THEME,
} from '../../../styles/config/extra-themes';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private appliedKeys: string[] = [];
  private isBrowser: boolean;

  private baseTheme: ThemeTokens = DISCORD_THEME;
  private activeModifier: ThemeTokens = {};
  private customOverrides: ThemeTokens = {};

  public readonly isDarkTheme = signal(false);
  public readonly activeBaseTheme = signal('discord');

  private readonly bases: Record<string, ThemeTokens> = {
    default: DEFAULT_THEME,
    minimal: MINIMAL_THEME,
    cupertino: CUPERTINO_THEME,
    discord: DISCORD_THEME,
  };

  private readonly darkModifiers: Record<string, ThemeTokens> = {
    default: DARK_THEME,
    minimal: DARK_THEME,
    cupertino: CUPERTINO_DARK_THEME,
    discord: DISCORD_DARK_THEME,
  };

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);

    // Default application-wide launch with Discord theme active
    if (this.isBrowser) {
      this.applyTheme(this.baseTheme);
      // Load custom overrides from localStorage
      const savedCustom = localStorage.getItem('theme_custom_overrides');
      if (savedCustom) {
        try {
          this.customOverrides = JSON.parse(savedCustom);
          this.applyTheme(this.customOverrides);
        } catch {
          this.customOverrides = {};
        }
      }
    }
  }

  /**
   * Switch between built-in base themes and automatically re-apply dark modifiers if active.
   */
  setActiveBase(name: string, event?: MouseEvent): void {
    const key = name ? name.toLowerCase() : 'default';
    if (this.bases[key]) {
      this.activeBaseTheme.set(key);
      this.baseTheme = this.bases[key];
      if (this.isDarkTheme()) {
        this.activeModifier = this.darkModifiers[key] || DARK_THEME;
      } else {
        this.activeModifier = {};
      }
      this._reapply(event);
    }
  }
  /**
   * Toggle dark mode using the correct modifier for the active base theme.
   */
  toggleDarkMode(event?: MouseEvent): void {
    this.isDarkTheme.update((d) => !d);
    this._applyDarkModifier(event);
  }

  private _applyDarkModifier(event?: MouseEvent): void {
    if (this.isDarkTheme()) {
      const modifier = this.darkModifiers[this.activeBaseTheme()] || DARK_THEME;
      this.setModifier(modifier, event);
    } else {
      this.clearModifier(event);
    }
  }

  /**
   * Set the base aesthetic theme layer (e.g. Brand variables).
   */
  setBaseTheme(tokens: ThemeTokens, event?: MouseEvent): void {
    this.baseTheme = tokens;
    this._reapply(event);
  }

  /**
   * Add a modifier theme layer (e.g. Dark Mode) on top of the base layer.
   */
  setModifier(tokens: ThemeTokens, event?: MouseEvent): void {
    this.activeModifier = tokens;
    this._reapply(event);
  }

  /**
   * Clear any active modifiers, returning exactly to the base theme.
   */
  clearModifier(event?: MouseEvent): void {
    this.activeModifier = {};
    this._reapply(event);
  }

  /**
   * Re-evaluates merging baseTheme, activeModifier, and customOverrides, and applies them.
   */
  private _reapply(event?: MouseEvent): void {
    const combined = {
      ...this.baseTheme,
      ...this.activeModifier,
      ...this.customOverrides,
    };

    if (!this.isBrowser || !(document as any).startViewTransition || !event) {
      this._clearAppliedTokens();
      this.applyTheme(combined);
      return;
    }

    const x = event.clientX;
    const y = event.clientY;
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y),
    );

    const transition = (document as any).startViewTransition(() => {
      this._clearAppliedTokens();
      this.applyTheme(combined);
    });

    transition.ready.then(() => {
      document.documentElement.animate(
        [
          { clipPath: `circle(0px at ${x}px ${y}px)` },
          { clipPath: `circle(${endRadius}px at ${x}px ${y}px)` },
        ],
        {
          duration: 400,
          easing: 'ease-in',
          pseudoElement: '::view-transition-new(root)',
        },
      );
    }).catch(() => {
      // Transition was skipped/aborted (e.g. superseded by a newer one) — ignore.
    });
  }

  /**
   * Apply a full or partial theme.
   * Only the provided keys will be overridden; everything else keeps
   * its default from _tokens.css.
   *
   * @param tokens — Partial<ThemeTokens> from backend JSON or a preset
   */
  applyTheme(tokens: ThemeTokens): void {
    if (!this.isBrowser) return;

    const root = document.documentElement;

    Object.entries(tokens).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        root.style.setProperty(key, value);
        if (!this.appliedKeys.includes(key)) {
          this.appliedKeys.push(key);
        }
      }
    });
  }

  /**
   * Set a single CSS custom property at runtime.
   */
  setToken(key: keyof ThemeTokens | `--app-${string}` | `--mbank-${string}` | `--color-${string}`, value: string): void {
    if (!this.isBrowser) return;

    (this.customOverrides as any)[key] = value;
    localStorage.setItem(
      'theme_custom_overrides',
      JSON.stringify(this.customOverrides),
    );

    document.documentElement.style.setProperty(key, value);
    if (!this.appliedKeys.includes(key)) {
      this.appliedKeys.push(key);
    }
  }

  /**
   * Get the current computed value of a token.
   */
  getToken(key: keyof ThemeTokens | `--app-${string}` | `--mbank-${string}` | `--color-${string}`): string {
    if (!this.isBrowser) return '';

    return getComputedStyle(document.documentElement)
      .getPropertyValue(key)
      .trim();
  }

  private _clearAppliedTokens(): void {
    if (!this.isBrowser) return;
    const root = document.documentElement;
    this.appliedKeys.forEach((key) => {
      root.style.removeProperty(key);
    });
    this.appliedKeys = [];
  }

  /**
   * Remove all runtime overrides (including custom).
   * Elements revert to the defaults defined in _tokens.css.
   */
  resetTheme(): void {
    this._clearAppliedTokens();
    this.customOverrides = {};
    if (this.isBrowser) {
      localStorage.removeItem('theme_custom_overrides');
    }
  }

  /**
   * Check if a theme (any overrides) is currently applied.
   */
  hasOverrides(): boolean {
    return this.appliedKeys.length > 0;
  }

  /**
   * Get all currently applied override keys.
   */
  getAppliedKeys(): readonly string[] {
    return [...this.appliedKeys];
  }

  // ══════════════════════════════════════════════════════════════
  // Legacy Compatibility Helpers
  // ══════════════════════════════════════════════════════════════

  /**
   * Compatibility helper for setting theme mode ('light', 'dark', 'system') or preset base theme.
   */
  setTheme(themeNameOrMode: string, event?: MouseEvent): void {
    if (!themeNameOrMode) return;
    const key = themeNameOrMode.toLowerCase();
    if (key === 'dark') {
      if (!this.isDarkTheme()) {
        this.toggleDarkMode(event);
      }
    } else if (key === 'light') {
      if (this.isDarkTheme()) {
        this.toggleDarkMode(event);
      }
    } else if (key === 'system') {
      const prefersDark = this.isBrowser && window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (this.isDarkTheme() !== prefersDark) {
        this.toggleDarkMode(event);
      }
    } else if (this.bases[key]) {
      this.setActiveBase(key, event);
    }
  }

  /**
   * Compatibility helper to toggle dark/light theme mode.
   */
  toggleTheme(event?: MouseEvent): void {
    this.toggleDarkMode(event);
  }

  /**
   * Compatibility helper to check if dark theme is active.
   */
  isDarkMode(): boolean {
    return this.isDarkTheme();
  }

  /**
   * Compatibility helper to load user saved theme preferences.
   */
  loadTheme(): void {
    if (!this.isBrowser) return;
    const savedCustom = localStorage.getItem('theme_custom_overrides');
    if (savedCustom) {
      try {
        const parsed = JSON.parse(savedCustom);
        this.applyTheme(parsed);
      } catch {
        // Ignore
      }
    }
  }
}
